import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { OBSERVATION_SITES } from '@/config/eclipse'
import { useI18n } from '@/i18n'
import { formatDuration } from '@/lib/countdown'
import { formatCoordinates } from '@/lib/datetime'

/**
 * Candidate observation sites along the path of totality.
 *
 * Deliberately shows no local contact time. Totality reaches each site at a
 * different instant, and converting the Makkah instant into another zone would
 * produce a plausible-looking but wrong clock time. The row returns once
 * per-site contact times are computed — see `docs/PRD.md`.
 */
export function SiteList() {
  const { t, pick } = useI18n()

  return (
    <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {OBSERVATION_SITES.map((site) => (
        <li key={site.id} className="h-full">
          <Card interactive className="flex h-full flex-col">
            <div className="flex flex-wrap items-center gap-2">
              {site.isPrimary ? <Badge tone="gold">{t.common.primaryBadge}</Badge> : null}
              <Badge tone={site.dataStatus === 'verified' ? 'gold' : 'muted'}>
                {site.dataStatus === 'verified' ? t.common.verifiedBadge : t.common.draftBadge}
              </Badge>
            </div>

            <h3 className="mt-4 text-2xl text-parchment">{site.city}</h3>
            <p className="text-sm text-muted">{pick(site.country)}</p>

            <dl className="mt-5 space-y-2.5 border-t border-line/70 pt-5 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-faint">{t.expedition.duration}</dt>
                <dd className="tabular font-serif text-base text-gold">
                  {formatDuration(site.totalitySeconds)}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-faint">{t.expedition.sunAltitude}</dt>
                <dd className="tabular text-parchment">{site.sunAltitudeDeg}°</dd>
              </div>
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <dt className="text-faint">{t.expedition.coordinates}</dt>
                <dd className="tabular text-xs text-muted">
                  {formatCoordinates(site.coordinates.lat, site.coordinates.lon)}
                </dd>
              </div>
            </dl>
          </Card>
        </li>
      ))}
    </ul>
  )
}
